import axios from "axios";
import CODES from "./StatusCodes";
import { store } from "Redux/Store";
import { logOutUser } from "Redux/Auth/Actions";
import { showServerError } from "Redux/General/Actions";

import { isIEBrowser } from "Helpers/Utils";
import { notification } from "antd";

const METHOD = {
	GET: "get",
	POST: "post",
	PUT: "put",
	DELETE: "delete",
	PATCH: "patch",
};

const BASEURL = process.env.REACT_APP_API_URL;

// Every request gets a hard ceiling. Without this a stalled connection stays in
// "pending" forever, the caller's `await` never returns and the screen is stuck
// on its loading/empty state with no way out. See Bug 1 / Bug 5 of the audit.
const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 30000;

// Client error codes used when the failure never reached the server, so callers
// can tell "the server said no" from "we could not reach the server".
export const CLIENT_ERROR = {
	NETWORK: 0,
	TIMEOUT: -1,
	UNKNOWN: -2,
};

// ---------------------------------------------------------------------------
// In-flight request coalescing + short-lived reference-data cache.
//
// Several screens mount sibling components that each ask for the same reference
// data (class years, teacher lists, assigned classes). Previously each mount
// opened its own connection. Identical GETs that overlap in time now share a
// single network round trip.
// ---------------------------------------------------------------------------
const inFlight = new Map();
const responseCache = new Map();

function buildKey(method, url, params, token) {
	let serialisedParams = "";
	try {
		serialisedParams = params ? JSON.stringify(params) : "";
	} catch (e) {
		serialisedParams = String(params);
	}
	// The token is part of the key so a cached/in-flight response can never leak
	// across a logout or across two accounts sharing the same tab.
	return `${method}|${url}|${serialisedParams}|${token || "anon"}`;
}

function readCache(key) {
	const hit = responseCache.get(key);
	if (!hit) return undefined;
	if (hit.expiresAt <= Date.now()) {
		responseCache.delete(key);
		return undefined;
	}
	return hit.value;
}

/**
 * Turn any axios failure into the same envelope shape the API returns on
 * success, so callers can keep using `if (response.status === 200)` and will
 * reliably fall through to their else-branch instead of hanging.
 */
export function normalizeError(error) {
	// api() rejects the 401/500 branches with the axios *response* object itself,
	// which carries `status`/`data` directly rather than nesting them under
	// `.response`. Recognise that shape first - it also has a `.request`, so
	// without this it would be misreported as a network failure.
	if (error && typeof error.status === "number" && !error.response) {
		return {
			status: error.status,
			message: error.data && error.data.message ? error.data.message : "Request failed.",
			data: error.data ? error.data.data : null,
			isError: true,
		};
	}

	if (error && error.response) {
		return {
			status: error.response.status,
			message: error.response.data && error.response.data.message ? error.response.data.message : "Request failed.",
			data: error.response.data ? error.response.data.data : null,
			isError: true,
		};
	}

	if (error && (error.code === "ECONNABORTED" || `${error.message || ""}`.toLowerCase().includes("timeout"))) {
		return {
			status: CLIENT_ERROR.TIMEOUT,
			message: "The server took too long to respond. Please try again.",
			data: null,
			isError: true,
			isTimeout: true,
		};
	}

	if (error && error.request) {
		return {
			status: CLIENT_ERROR.NETWORK,
			message: "Could not reach the server. Check your connection and try again.",
			data: null,
			isError: true,
			isNetworkError: true,
		};
	}

	return {
		status: CLIENT_ERROR.UNKNOWN,
		message: (error && error.message) || "Something went wrong.",
		data: null,
		isError: true,
	};
}

// CHECK BELOW FOR SAMPLE DATA TO BE PASSED
class Api {
	isLoggedIn = false;

	constructor() {
		this.baseURL = BASEURL;
		this.getAuthenticationInfo();
	}

	getAuthenticationInfo() {
		// Auth is kept in sessionStorage (per browser tab) instead of localStorage
		// so an admin session in one tab and a teacher session in another tab do
		// not overwrite each other's token on the same origin.
		const token = sessionStorage.getItem("cms_accessToken");
		if (sessionStorage.getItem("cms_isLoggedIn") && token) {
			this.isLoggedIn = true;
			this.accessToken = token;
		} else {
			this.isLoggedIn = false;
			this.accessToken = null;
		}
	}

	/** Drop cached reference data. Call after a mutation invalidates it. */
	static clearCache(urlPrefix) {
		if (!urlPrefix) {
			responseCache.clear();
			return;
		}
		Array.from(responseCache.keys()).forEach((key) => {
			if (key.indexOf(`|${urlPrefix}`) !== -1) responseCache.delete(key);
		});
	}

	// URL FOR API
	// REFER SAMPLE JSON AT BOTTOM FOR DATA VALUES
	//
	// GET/PUT/DELETE always RESOLVE - never reject and never hang. On failure they
	// resolve with a normalized error envelope carrying a non-200 `status`, so the
	// caller's existing else-branch runs and clears its loading state.
	get(url, data) {
		const key = buildKey(METHOD.GET, url, data && data.params, this.accessToken);
		const ttl = data && data.cacheTtl;

		if (ttl) {
			const cached = readCache(key);
			if (cached !== undefined) return Promise.resolve(cached);
		}

		if (!(data && data.skipDedupe)) {
			const pending = inFlight.get(key);
			if (pending) return pending;
		}

		const request = this.api(METHOD.GET, url, data)
			.then((response) => {
				if (ttl && response && (response.status === 200 || response.status === 201)) {
					responseCache.set(key, { value: response, expiresAt: Date.now() + ttl });
				}
				return response;
			})
			.catch((error) => normalizeError(error))
			.then((result) => {
				inFlight.delete(key);
				return result;
			});

		if (!(data && data.skipDedupe)) inFlight.set(key, request);
		return request;
	}

	post(url, data) {
		// POST/PATCH keep their reject-on-failure contract - callers wrap them in
		// try/catch and rely on the throw.
		return this.api(METHOD.POST, url, data);
	}

	put(url, data) {
		return this.api(METHOD.PUT, url, data).catch((error) => normalizeError(error));
	}

	delete(url, data) {
		return this.api(METHOD.DELETE, url, data).catch((error) => normalizeError(error));
	}

	patch(url, data) {
		return this.api(METHOD.PATCH, url, data);
	}

	api(method, url, data) {
		return new Promise((resolve, reject) => {
			let axiosConfig = {};
			axiosConfig.method = method;

			axiosConfig.url = this.baseURL + url;

			axiosConfig.headers = this.setHeaders(data);
			axiosConfig.timeout = (data && data.timeout) || DEFAULT_TIMEOUT_MS;

			if (data) {
				if (data.params) axiosConfig.params = data.params;

				if (data.data) axiosConfig.data = data.data;

				if (data.signal) axiosConfig.signal = data.signal;
				if (data.cancelToken) axiosConfig.cancelToken = data.cancelToken;
			}

			if (isIEBrowser()) {
				if (!axiosConfig.params) axiosConfig.params = {};
				axiosConfig.params.time = new Date().getTime();
			}

			axios(axiosConfig)
				.then((response) => {
					resolve(response.data);
				})
				.catch((error) => {
					// A request the caller cancelled is not a failure worth reporting.
					if (axios.isCancel && axios.isCancel(error)) {
						resolve({ status: CLIENT_ERROR.UNKNOWN, message: "cancelled", isError: true, isCancelled: true });
						return;
					}

					if (error && error.response) {
						if (error.response.status === CODES.UNAUTHORIZED) {
							notification.error({ type: "error", message: "Error", description: error.response.data.message, duration: 6 });
							let role = store.getState().Auth.role;
							if (!role) {
								try {
									role = JSON.parse(sessionStorage.getItem("cms_userInfo"))?.roleName;
								} catch (e) {
									role = null;
								}
							}
							// If the role is not known yet (e.g. the status call itself 401'd), fall back
							// to the per-tab portal marker so a teacher/school user is still sent back to
							// the teacher portal instead of the admin login page.
							const fromTeacherPortal = sessionStorage.getItem("cms_loginPortal") === "teacher";
							store.dispatch(logOutUser());
							if (role === "teacher" || role === "school" || fromTeacherPortal) {
								window.location.replace(`${process.env.REACT_APP_WEBSITE_URL}teacher/login`);
							}
							reject(error.response);
						} else if (error.response.status === CODES.SERVER_ERROR) {
							if (data && !data.skipToast) notification.error({ type: "error", message: "Error", description: error.response.data.message, duration: 6 });

							store.dispatch(showServerError());
							if (data && data.skipErrorHandling) notification.error({ type: "error", message: "Error", description: error.response.data.message, duration: 6 });
							reject(error.response);
						} else if (error.response.status === CODES.LOCKED) {
							resolve(error.response);
						} else if (error.response.status === CODES.BANDWIDTH_LIMIT_EXCEEDED) {
							resolve(error.response);
						} else {
							if (!(data && data.skipToast)) {
								notification.error({ message: "Error", description: error.response.data && error.response.data.message ? error.response.data.message : "Request failed.", duration: 3 });
							}
							resolve(error.response);
						}
						return;
					}

					// No response at all: network down, CORS, DNS, or our own timeout.
					// This branch previously only logged, leaving the promise pending
					// forever and the calling screen stuck on "No Data Available".
					const normalized = normalizeError(error);
					if (!(data && data.skipToast)) {
						notification.error({ type: "error", message: "Error", description: normalized.message, duration: 5 });
					}
					reject(normalized);
				});
		});
	}

	setHeaders(data) {
		let headers = {};
		headers["accept-language"] = "en";
		headers["Content-Type"] = "application/json";
		if (data) {
			if (data.isMultipart) {
				headers["Content-Type"] = "multipart/form-data";
			}

			if (data.headers) {
				for (var key in data.headers) {
					if (data.headers.hasOwnProperty(key)) {
						headers[key] = data.headers[key];
					}
				}
			}
		}

		if (this.isLoggedIn && this.accessToken && !(data && data.skipAuth)) {
			headers["accessToken"] = this.accessToken;
		}

		return headers;
	}
}

// SAMPLE DATA JSON
/* let sample_data = {

    // ADDITIONAL HEADERS IF REQUIRED
    headers :{
        'Content-type'  : 'xx-urlencode',
    },

    // IF USER ID AND TOKEN SHOULDN'T BE PASSED IN HEADERS (USER FOR AFTER LOGIN API)
    // DEFAULT : FALSE;
    skipAuth    : false,

    // IF Default error handling needs to be overridden
    // DEFAULT : FALSE;
    skipErrorHandling    : false,

    // FOR SENDING FILES OR FORM DATA REQUEST
    isMultipart : true,

    // PER-REQUEST TIMEOUT IN MS (default 30000)
    timeout : 15000,

    // GET ONLY: cache a successful response for this many ms
    cacheTtl : 300000,

    // GET ONLY: opt out of in-flight de-duplication
    skipDedupe : false,

    // SUPPRESS THE AUTOMATIC ERROR TOAST (screen renders its own error state)
    skipToast : true,

    // `PARAMS` ARE THE URL PARAMETERS TO BE SENT WITH THE REQUEST
    params : {
        user_id     : 10,
        name        : "lorem",
        page        : 3,
        sort_by     : 'name'
    },

    // POST DATA
    data : {
        firstName   : 'Lorem',
        lastName    : 'Ipsum'
    },
} */

export default Api;
